import './AccountPages.scss'
import React, { useEffect, useState } from 'react'
import BEMHelper from 'react-bem-helper'
import { Formik } from 'formik'
import { useConnect } from 'redux-bundler-hook'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import Link from '@src/components/Link'
import useTranslations from '@src/hooks/useTranslations'
import validator from '@src/form/validations'
import {
	transformDateString,
	transformDateStringRidecell,
	transformSso,
} from '@src/form/transformation'
import FormContainer from '@src/components/FormContainer'
import { azureApiGatewayPost } from '@src/api/azureApiGateway/client'
import useSetting from '@src/hooks/useSetting'
import { googleAnalytics } from '@src/api/googleanalytics/googleAnalytics'
import ErrorModal from '@src/components/ErrorModal'
import { REGISTRATION_STEPS } from '@src/components/account/Account'

const bem = new BEMHelper('registration-page')

const useStyles = makeStyles({
	button: {
		marginTop: '2.5rem',
	},
})

function AddDriversLicensePage({ setNextStep }) {
	const t = useTranslations()
	const getSetting = useSetting()
	const classes = useStyles()
	const [errorMessage, showErrorMessage] = useState(null)

	const { doSetCustomerInfo, customerInfo, doRefreshCustomerInfo } = useConnect(
		'doSetCustomerInfo',
		'selectCustomerInfo',
		'doRefreshCustomerInfo',
	)

	const foreignLink = `${getSetting('registration_foreign_link', '/')}?id=${
		customerInfo.customerId
	}`
	const notPrivate = getSetting('registration_not_private')

	useEffect(() => {
		googleAnalytics.pushVirtualPageView(
			`/virtual/account/adddriverslicensepage/${customerInfo.customerId}`,
			`User with userid ${customerInfo.customerId} is adding drivers license`,
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const goToNext = async (sso, driverLicenseExpDate) => {
		driverLicenseExpDate = transformDateStringRidecell(driverLicenseExpDate)
		doSetCustomerInfo({ sso, driverLicenseExpDate })
		try {
			await azureApiGatewayPost(
				`customer/${customerInfo.customerId}/drivers_license/track`,
				{
					personal_number: sso,
					expiry_date: driverLicenseExpDate,
				},
				{ useToken: true, params: { registration: true } },
			)
			await doRefreshCustomerInfo()
			setNextStep(REGISTRATION_STEPS.CONFIRM_DRIVERS_LICENSE_AND_CREDIT_CHECK)
		} catch (error) {
			console.log(error)
			showErrorMessage(error.message)
		}
	}

	return (
		<div {...bem('')}>
			{errorMessage && (
				<ErrorModal onClose={() => showErrorMessage(null)}>
					{errorMessage}
				</ErrorModal>
			)}
			<Formik
				initialValues={{
					sso: customerInfo.sso || '',
					driverLicenseExpDate: '',
				}}
				validationSchema={validator({
					sso: { validatorKey: 'sso', required: true },
					driverLicenseExpDate: { validatorKey: 'date', required: true },
				})}
			>
				{(props) => {
					const {
						values,
						touched,
						errors,
						handleChange,
						handleBlur,
						dirty,
						isValid,
					} = props
					return (
						<>
							<FormContainer twoColumns>
								<TextField
									label={t('kinto.form.social-security-number')}
									id={t('kinto.form.social-security-number')}
									name='sso'
									variant='outlined'
									value={values.sso}
									onChange={handleChange}
									onBlur={(event) => {
										if (!errors.sso) {
											props.setFieldValue(
												'sso',
												transformSso(event.target.value),
											)
										}
										handleBlur(event)
									}}
									error={errors.sso && touched.sso}
									helperText={touched.sso && errors.sso}
								/>
								<TextField
									label={t('kinto.form.license-expiry-date')}
									id={t('kinto.form.license-expiry-date')}
									name='driverLicenseExpDate'
									variant='outlined'
									classes={{ root: classes.root }}
									value={values.driverLicenseExpDate}
									onChange={handleChange}
									onBlur={(event) => {
										if (!errors.driverLicenseExpDate) {
											props.setFieldValue(
												'driverLicenseExpDate',
												transformDateString(event.target.value),
											)
										}
										handleBlur(event)
									}}
									error={
										errors.driverLicenseExpDate && touched.driverLicenseExpDate
									}
									helperText={
										touched.driverLicenseExpDate && errors.driverLicenseExpDate
									}
								/>
							</FormContainer>
							<FormContainer>
								<Button
									variant='contained'
									aria-label={t('kinto.general.next')}
									classes={{ root: classes.button }}
									color='primary'
									disabled={!(isValid && dirty)}
									onClick={() =>
										goToNext(values.sso, values.driverLicenseExpDate)
									}
								>
									{t('kinto.general.next')}
								</Button>
								<Link to={foreignLink}>
									<div>{t('kinto.register.forign-drivers-license')}</div>
								</Link>
								{notPrivate !== '' &&
									<Link to={notPrivate} {...bem('not-private-link')}>
										<div>{t('kinto.register.not-private-link')}</div>
									</Link>
								}
							</FormContainer>
						</>
					)
				}}
			</Formik>
		</div>
	)
}

export default AddDriversLicensePage
